// Live multi-language preview of a room card — mirrors HotelContext.localizeRoom (translation needs a name, else English).
import React from 'react';
import { Eye, Users, Maximize } from 'lucide-react';

const resolve = (room, tr, code) => {
  if (code === 'en') return { name: room.name, description: room.description, amenities: room.amenities || [], fallback: null };
  const t = tr?.[code];
  if (!t?.name) return { name: room.name, description: room.description, amenities: room.amenities || [], fallback: 'shows English' };
  return {
    name: t.name,
    description: t.description || room.description,
    amenities: t.amenities || room.amenities || [],
    fallback: !t.description ? 'description in English' : !t.amenities ? 'amenities in English' : null,
  };
};

const Card = ({ code, label, room, data, active, onSelect }) => (
  <button
    type="button"
    onClick={() => code !== 'en' && onSelect(code)}
    className={`text-left bg-white rounded-xl overflow-hidden border-2 transition-all duration-200 ${active ? 'border-orange-500 shadow-md' : 'border-slate-200 hover:border-orange-300'} ${code === 'en' ? 'cursor-default' : ''}`}
    data-testid={`room-preview-${room.id}-${code}`}
  >
    <div className="relative aspect-[16/9] bg-slate-200">
      {room.images?.[0] && <img src={room.images[0]} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      <span className="absolute top-1.5 left-1.5 text-[10px] font-bold bg-black/60 text-white rounded px-1.5 py-0.5">{label}</span>
      {data.fallback && <span className="absolute top-1.5 right-1.5 text-[10px] bg-amber-100 text-amber-800 border border-amber-300 rounded px-1.5 py-0.5" data-testid={`room-preview-fallback-${room.id}-${code}`}>{data.fallback}</span>}
      <span className="absolute bottom-1.5 right-1.5 text-[11px] font-bold bg-white/90 text-slate-900 rounded px-1.5 py-0.5">€{room.price}</span>
    </div>
    <div className="p-2.5 space-y-1">
      <p className="text-sm font-bold text-slate-900 leading-tight line-clamp-2" data-testid={`room-preview-name-${room.id}-${code}`}>{data.name || '—'}</p>
      <p className="flex items-center gap-2 text-[10px] text-slate-500"><span className="inline-flex items-center gap-0.5"><Maximize className="w-3 h-3" />{room.size} m²</span><span className="inline-flex items-center gap-0.5"><Users className="w-3 h-3" />{room.guests}</span></p>
      <p className="text-[11px] text-slate-600 leading-snug line-clamp-3" data-testid={`room-preview-desc-${room.id}-${code}`}>{data.description || '—'}</p>
      <div className="flex flex-wrap gap-1 pt-0.5">
        {(data.amenities || []).slice(0, 4).map((a) => <span key={a} className="text-[9px] bg-slate-100 text-slate-700 rounded px-1 py-0.5">{a}</span>)}
      </div>
    </div>
  </button>
);

export const RoomCopyPreview = ({ room, translations, langs, activeLang, onSelect, dirty }) => (
  <div className="border-t pt-3 mt-1" data-testid={`room-copy-preview-${room.id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
      <span className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5 text-orange-500" /> Live room-card preview — all languages</span>
      <span className={`text-[11px] rounded-full px-2 py-0.5 border ${dirty ? 'bg-orange-50 text-orange-700 border-orange-200' : 'bg-green-50 text-green-700 border-green-200'}`} data-testid={`room-preview-state-${room.id}`}>
        {dirty ? 'Unsaved edits — press Save translations' : 'Matches what visitors see now'}
      </span>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-2">
      <Card code="en" label="EN" room={room} data={resolve(room, translations, 'en')} active={false} onSelect={onSelect} />
      {langs.map(([code, label]) => (
        <Card key={code} code={code} label={code.toUpperCase()} room={room} data={resolve(room, translations, code)} active={activeLang === code} onSelect={onSelect} />
      ))}
    </div>
  </div>
);

export default RoomCopyPreview;
